import { useState } from 'react';
import { useAuthStore } from '@munawb/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@munawb/supabase';
import type { QualityRcaRow, IncidentRow } from '@munawb/types';
import { RISK_LEVEL_COLORS, formatDate } from '../lib/utils';

type RcaWithIncident = QualityRcaRow & {
  incidents: Pick<IncidentRow, 'event_id' | 'event_type' | 'risk_level' | 'location' | 'description'> | null;
};

type TabId = 'pending' | 'rca-list' | 'new-rca';

export default function QualityPage() {
  const { profile }  = useAuthStore();
  const qc           = useQueryClient();
  const [activeTab, setActiveTab]         = useState<TabId>('pending');
  const [selectedRca, setSelectedRca]     = useState<RcaWithIncident | null>(null);
  const [newRcaIncidentId, setNewRcaIncidentId] = useState('');

  // Incidents needing RCA
  const { data: pendingIncidents = [], isLoading: pendingLoading } = useQuery<IncidentRow[]>({
    queryKey: ['incidents-rca-pending'],
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('incidents')
        .select('*')
        .eq('status', 'طلب RCA')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // All RCAs
  const { data: rcaList = [], isLoading: rcaLoading } = useQuery<RcaWithIncident[]>({
    queryKey: ['quality-rca-list'],
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('quality_rca')
        .select('*, incidents(event_id, event_type, risk_level, location, description)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as RcaWithIncident[];
    },
  });

  // Save/Update RCA
  const { mutateAsync: saveRca, isPending: saving } = useMutation({
    mutationFn: async (payload: Partial<QualityRcaRow> & { incident_id: string }) => {
      if (payload.id) {
        const { error } = await supabase
          .from('quality_rca')
          .update(payload)
          .eq('id', payload.id);
        if (error) throw error;
      } else {
        const rcaId = `RCA-${payload.incident_id.slice(0, 8).toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
        const { error } = await supabase
          .from('quality_rca')
          .insert({ ...payload, rca_id: rcaId, analyst_id: profile?.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quality-rca-list'] });
      qc.invalidateQueries({ queryKey: ['incidents-rca-pending'] });
      setSelectedRca(null);
    },
  });

  const handleSignOut = async () => { await supabase.auth.signOut(); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950">

      {/* Header */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-8 h-8 rounded-xl" />
            <span className="font-black text-slate-800 dark:text-white text-sm">مُناوِب — الجودة</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{profile?.name}</span>
            <button onClick={handleSignOut} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {([
            { id: 'pending',  label: `طلبات RCA (${pendingIncidents.length})` },
            { id: 'rca-list', label: 'قائمة RCA' },
          ] as { id: TabId; label: string }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl font-black text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Pending RCA Tab */}
        {activeTab === 'pending' && (
          <div className="space-y-3">
            {pendingLoading ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <span className="spinner border-blue-200 border-t-blue-600" />
              </div>
            ) : pendingIncidents.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <p className="text-slate-400 font-bold text-sm">لا توجد طلبات RCA معلقة</p>
              </div>
            ) : (
              pendingIncidents.map(inc => {
                const colors = RISK_LEVEL_COLORS[inc.risk_level] ?? RISK_LEVEL_COLORS['منخفض'];
                const existing = rcaList.find(r => r.incident_id === inc.id);
                return (
                  <div key={inc.id} className="glass-panel rounded-2xl p-5 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-black text-slate-400">{inc.event_id}</span>
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${colors.badge}`}>
                          {inc.risk_level}
                        </span>
                      </div>
                      <p className="text-sm font-black text-slate-800 dark:text-white">{inc.event_type}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{inc.location}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{formatDate(inc.created_at)}</p>
                    </div>
                    <button
                      onClick={() => {
                        if (existing) {
                          setSelectedRca(existing as RcaWithIncident);
                        } else {
                          setNewRcaIncidentId(inc.id);
                          setActiveTab('new-rca' as TabId);
                        }
                      }}
                      className={existing ? 'btn-secondary text-xs' : 'btn-primary text-xs'}
                    >
                      {existing ? 'تعديل RCA' : 'بدء التحليل'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* RCA List Tab */}
        {activeTab === 'rca-list' && (
          <div className="space-y-3">
            {rcaLoading ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <span className="spinner border-blue-200 border-t-blue-600" />
              </div>
            ) : rcaList.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <p className="text-slate-400 font-bold text-sm">لا توجد تحليلات RCA</p>
              </div>
            ) : (
              rcaList.map(rca => (
                <button
                  key={rca.id}
                  onClick={() => setSelectedRca(rca)}
                  className="glass-panel rounded-2xl p-5 w-full text-right hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black text-slate-400">{rca.rca_id}</p>
                      <p className="text-sm font-black text-slate-800 dark:text-white mt-0.5">
                        {rca.incidents?.event_type ?? '—'}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{rca.incidents?.event_id ?? '—'}</p>
                    </div>
                    <div>
                      <span className={`text-xs font-black px-2.5 py-1 rounded-full ${
                        rca.status === 'مكتمل' || rca.status === 'Completed'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                      }`}>
                        {rca.status}
                      </span>
                    </div>
                  </div>
                  {rca.root_cause && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 line-clamp-1">
                      السبب الجذري: {rca.root_cause}
                    </p>
                  )}
                </button>
              ))
            )}
          </div>
        )}

        {/* New RCA Tab */}
        {activeTab === ('new-rca' as TabId) && newRcaIncidentId && (
          <RcaForm
            incidentId={newRcaIncidentId}
            analystId={profile?.id ?? ''}
            onSave={saveRca}
            saving={saving}
            onCancel={() => { setActiveTab('pending'); setNewRcaIncidentId(''); }}
          />
        )}
      </main>

      {/* RCA Edit Modal */}
      {selectedRca && (
        <RcaModal
          rca={selectedRca}
          onSave={saveRca}
          saving={saving}
          onClose={() => setSelectedRca(null)}
        />
      )}
    </div>
  );
}

/* ── RCA Form ─────────────────────────────────────────────────── */

interface RcaFormProps {
  incidentId: string;
  analystId:  string;
  rca?:       Partial<QualityRcaRow>;
  onSave:     (payload: Partial<QualityRcaRow> & { incident_id: string }) => Promise<void>;
  saving:     boolean;
  onCancel:   () => void;
}

function RcaForm({ incidentId, rca, onSave, saving, onCancel }: RcaFormProps) {
  const [form, setForm] = useState({
    why_1:       rca?.why_1       ?? '',
    why_2:       rca?.why_2       ?? '',
    why_3:       rca?.why_3       ?? '',
    why_4:       rca?.why_4       ?? '',
    root_cause:  rca?.root_cause  ?? '',
    action_plan: rca?.action_plan ?? '',
    owner:       rca?.owner       ?? '',
    deadline:    rca?.deadline    ?? '',
    status:      rca?.status      ?? 'قيد التحليل',
  });

  const handleSave = async () => {
    await onSave({ incident_id: incidentId, id: rca?.id, ...form });
  };

  const Field = ({ label, field, rows = 1 }: { label: string; field: keyof typeof form; rows?: number }) => (
    <div>
      <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">{label}</label>
      {rows > 1 ? (
        <textarea
          rows={rows}
          className="input-field resize-none"
          value={form[field]}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        />
      ) : (
        <input
          type="text"
          className="input-field"
          value={form[field]}
          onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        />
      )}
    </div>
  );

  return (
    <div className="glass-panel rounded-2xl p-6 space-y-4">
      <h2 className="text-base font-black text-slate-800 dark:text-white">تحليل السبب الجذري (5 لماذا)</h2>
      <Field label="لماذا #1" field="why_1" />
      <Field label="لماذا #2" field="why_2" />
      <Field label="لماذا #3" field="why_3" />
      <Field label="لماذا #4" field="why_4" />
      <Field label="السبب الجذري" field="root_cause" rows={3} />
      <Field label="خطة الإجراء" field="action_plan" rows={3} />
      <div className="grid grid-cols-2 gap-4">
        <Field label="المسؤول" field="owner" />
        <div>
          <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">الموعد النهائي</label>
          <input
            type="date"
            className="input-field"
            dir="ltr"
            value={form.deadline}
            onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-black text-slate-600 dark:text-slate-400 mb-1.5">الحالة</label>
        <select className="input-field" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
          <option value="قيد التحليل">قيد التحليل</option>
          <option value="مكتمل">مكتمل</option>
        </select>
      </div>
      <div className="flex gap-3">
        <button onClick={onCancel} className="btn-secondary flex-1">إلغاء</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary flex-1">
          {saving ? <span className="spinner border-white/30 border-t-white" /> : 'حفظ التحليل'}
        </button>
      </div>
    </div>
  );
}

function RcaModal({ rca, onSave, saving, onClose }: {
  rca: RcaWithIncident;
  onSave: RcaFormProps['onSave'];
  saving: boolean;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className="glass-panel w-full max-w-2xl rounded-2xl p-6 shadow-2xl my-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-black text-slate-800 dark:text-white">{rca.rca_id}</h3>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <RcaForm
          incidentId={rca.incident_id}
          analystId={rca.analyst_id ?? ''}
          rca={rca}
          onSave={onSave}
          saving={saving}
          onCancel={onClose}
        />
      </div>
    </div>
  );
}
